import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
  readNarrativeType,
  shareNarrativeWithClientGroup,
  shareNarrativeWithUsers,
  updateNarrativeName,
} from '@api/narratives';
import { narrativeStatus, setEndpoint } from '@helpers';
import {
  DownloadV2Icon,
  EditableTitle,
  HelpPlainIcon,
  IconButtonV2,
  RepromptV2Icon,
  ShareV2Icon,
  toast,
  Toolbar,
} from '@utilities';

import ModalShare from '../../../ModalShare';
import Download from '../../../Builder/components/Toolbar/components/Download';
import SaveAsTemplate from '../../../SaveAsTemplate';
import TitleFooter from './components/TitleFooter';
import { filterPrompts } from '../../../../pages/Narratives/components/ModalBuildNarrative/utilities/helpers';
import {
  buildNarrativeDownloadableData,
  buildNarrativeDownloadFileName,
  getHelpArticleURLByNarrativeId,
} from '../../utilities/helpers';

import styles from './_index.module.scss';

const NarrativeTitle = ({
  downloadData,
  flowData,
  handleReprompt,
  isDraft = false,
  onNarrativeNameChange,
  onTemplateSaved,
  publicToken,
  selectedPromptsOverride = null,
  setFlow,
  titleOverride = null,
  topicIds = null,
}) => {
  const [name, setName] = useState(null);
  const [newName, setNewName] = useState(null);
  const [showModalShare, setShowModalShare] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [isDownloadDropdownActive, setIsDownloadDropdownActive] = useState(false);

  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { canAccessBuildYourOwn, canAccessChat, canShareReportsPublicly } = permissions;
  const canAccessSaveTemplate = canAccessBuildYourOwn || canAccessChat;
  const canReprompt =
    flowData?.narrative_id !== 'dynamic' || canAccessBuildYourOwn || canAccessChat;
  const repromptTooltip = canReprompt
    ? 'Reprompt'
    : 'Reprompting this narrative requires additional access.';

  const isPublicShare = Boolean(publicToken);

  const downloadRef = useRef(null);

  const downloadFileUrl = setEndpoint(`api/flows/${flowData?.id}/export`);

  const downloadableData = useMemo(() => {
    return buildNarrativeDownloadableData({ downloadData, flowData });
  }, [downloadData, flowData]);

  const customFileName = useMemo(() => {
    return buildNarrativeDownloadFileName(flowData);
  }, [flowData]);

  const isDownloadAvailable = flowData?.status === narrativeStatus.COMPLETED && downloadableData;
  const downloadTooltip = isDownloadAvailable
    ? 'Download'
    : 'Download will be available after report has finished processing';

  const shareTooltip =
    flowData?.status === narrativeStatus.COMPLETED
      ? 'Share'
      : 'Share will be available after report has finished processing';

  const normalizedExternalTopicIds = useMemo(() => {
    if (!Array.isArray(topicIds)) return [];
    return topicIds.filter(
      (topicId) => topicId != null && (typeof topicId !== 'string' || topicId.trim().length > 0)
    );
  }, [topicIds]);
  const normalizedFlowTopicIds = useMemo(() => {
    if (!Array.isArray(flowData?.topic_ids)) return [];
    return flowData.topic_ids.filter(
      (topicId) => topicId != null && (typeof topicId !== 'string' || topicId.trim().length > 0)
    );
  }, [flowData?.topic_ids]);
  const normalizedFlowDerivedTopicIds = useMemo(() => {
    if (!Array.isArray(flowData?.topics)) return [];
    return flowData.topics
      .filter((topic) => !topic?.isHidden)
      .map((topic) => topic?.topicId)
      .filter(
        (topicId) => topicId != null && (typeof topicId !== 'string' || topicId.trim().length > 0)
      );
  }, [flowData?.topics]);
  const saveTemplateTopicIds = useMemo(() => {
    if (normalizedExternalTopicIds.length > 0) return normalizedExternalTopicIds;
    if (normalizedFlowTopicIds.length > 0) return normalizedFlowTopicIds;
    return normalizedFlowDerivedTopicIds;
  }, [normalizedExternalTopicIds, normalizedFlowDerivedTopicIds, normalizedFlowTopicIds]);
  const dynamicTopicPrompts = useMemo(() => {
    if (flowData?.narrative_id !== 'dynamic') return null;
    if (normalizedExternalTopicIds.length > 0) return normalizedExternalTopicIds;
    if (normalizedFlowTopicIds.length > 0) return normalizedFlowTopicIds;
    return null;
  }, [flowData?.narrative_id, normalizedExternalTopicIds, normalizedFlowTopicIds]);
  const dynamicTopicPromptsSignature = useMemo(
    () => JSON.stringify(dynamicTopicPrompts || []),
    [dynamicTopicPrompts]
  );
  const parsedDynamicTopicPrompts = useMemo(
    () => (dynamicTopicPromptsSignature === '[]' ? null : JSON.parse(dynamicTopicPromptsSignature)),
    [dynamicTopicPromptsSignature]
  );

  useEffect(() => {
    const isDynamic = flowData?.narrative_id === 'dynamic';

    if ((isDraft && !isDynamic) || !flowData?.id || !flowData?.narrative_id) return;

    const initializeSelectedPrompts = async () => {
      try {
        const hasDynamicTopicPrompts = Array.isArray(parsedDynamicTopicPrompts);

        const data = await readNarrativeType({
          flowId: hasDynamicTopicPrompts ? null : flowData.id,
          narrativeId: flowData?.narrative_id,
          publicToken: publicToken,
          topicIds: hasDynamicTopicPrompts ? parsedDynamicTopicPrompts : null,
        });

        const prompts = data?.prompts || [];
        const grouping = data?.promptGrouping || (hasDynamicTopicPrompts ? null : undefined);
        setSelectedPrompts(filterPrompts(prompts, grouping));
      } catch (error) {
        console.error('Error fetching Narrative prompts:', error);
        toast.error('Failed to load prompts.');
      }
    };

    initializeSelectedPrompts();
  }, [flowData?.id, flowData?.narrative_id, isDraft, parsedDynamicTopicPrompts, publicToken]);

  useEffect(() => {
    if (!newName) return;
    const nextName = newName;
    setNewName(null);

    if (isDraft) {
      setFlow((prev) => ({ ...prev, name: nextName }));
      return;
    }

    if (!flowData?.id) return;

    const updateFlowName = async () => {
      try {
        await updateNarrativeName({ id: flowData.id, name: nextName });
        setFlow((prev) => ({ ...prev, name: nextName }));
        if (typeof onNarrativeNameChange === 'function') {
          onNarrativeNameChange({ flowId: flowData.id, name: nextName });
        }
      } catch (error) {
        console.error(error);
      }
    };

    updateFlowName();
  }, [flowData?.id, isDraft, newName, onNarrativeNameChange, setFlow]);

  const footerSelectedPrompts =
    Array.isArray(selectedPromptsOverride) && selectedPromptsOverride.length > 0
      ? selectedPromptsOverride
      : selectedPrompts;
  const titleValue = titleOverride ?? flowData?.name;

  return (
    <div className={styles['narrative-title-container']}>
      <div className={styles['narrative-title-toolbar-wrapper']}>
        {!publicToken ? (
          <Toolbar
            className={styles['narrative-title-toolbar']}
            tools={[
              <IconButtonV2
                aria-label="Share"
                className={styles['action-icon']}
                data-testid="share-button"
                disabled={flowData?.status !== narrativeStatus.COMPLETED}
                icon={<ShareV2Icon />}
                key="share"
                onClick={() => setShowModalShare(true)}
                tooltip={shareTooltip}
              />,
              <span key="download" ref={downloadRef}>
                <>
                  <IconButtonV2
                    aria-label="Download"
                    className={styles['action-icon']}
                    data-testid="download-dropdown-button"
                    disabled={!isDownloadAvailable}
                    icon={<DownloadV2Icon />}
                    onClick={() => setIsDownloadDropdownActive(true)}
                    tooltip={downloadTooltip}
                  />
                  <Download
                    customFileName={customFileName}
                    downloadData={downloadableData}
                    downloadFileUrl={downloadFileUrl}
                    downloadLabel={
                      flowData?.narrativeName || flowData?.narrative_name || 'Narrative'
                    }
                    isAvailable={isDownloadAvailable}
                    isDownloadDropdownActive={isDownloadDropdownActive}
                    setIsDownloadDropdownActive={setIsDownloadDropdownActive}
                    target={downloadRef?.current}
                  />
                </>
              </span>,
              <IconButtonV2
                aria-label="Reprompt"
                className={styles['action-icon']}
                data-testid="reprompt-button"
                disabled={isDraft || !canReprompt}
                icon={<RepromptV2Icon />}
                key="reprompt"
                onClick={canReprompt ? handleReprompt : undefined}
                tooltip={repromptTooltip}
              />,
              ...(canAccessSaveTemplate
                ? [
                    <SaveAsTemplate
                      canAccessSaveTemplate={canAccessSaveTemplate}
                      isPublicShare={isPublicShare}
                      key="save-template"
                      onTemplateSaved={onTemplateSaved}
                      topicIds={saveTemplateTopicIds}
                      triggerClassName={styles['action-icon']}
                      triggerVariant="icon"
                    />,
                  ]
                : []),
              <IconButtonV2
                aria-label="Help"
                as="a"
                className={styles['action-icon']}
                data-log="Flow_Help_Center"
                data-log-properties={JSON.stringify({
                  id: flowData?.id,
                  type: flowData?.narrative_id,
                })}
                data-testid="help-button"
                href={getHelpArticleURLByNarrativeId(flowData?.narrative_id)}
                icon={<HelpPlainIcon />}
                key="help"
                rel="noreferrer"
                target="_blank"
                tooltip="Help"
              />,
            ]}
          >
            {!isDraft ? (
              <EditableTitle
                autoFocus={false}
                className={styles['narrative-title']}
                isDisabled={flowData?.isReadOnly || !canReprompt}
                onBlur={setNewName}
                onChange={setName}
                value={name !== null ? name : titleValue}
              />
            ) : (
              <EditableTitle
                autoFocus={false}
                className={styles['narrative-title']}
                isDisabled={false}
                onBlur={setNewName}
                onChange={setName}
                value={name !== null ? name : titleValue}
              />
            )}
          </Toolbar>
        ) : (
          <Toolbar
            className={styles['narrative-title-toolbar']}
            tools={[
              <IconButtonV2
                aria-label="Help"
                as="a"
                className={styles['action-icon']}
                data-log="Flow_Help_Center"
                data-log-properties={JSON.stringify({
                  id: flowData?.id,
                  type: flowData?.narrative_id,
                })}
                data-testid="help-button"
                href={getHelpArticleURLByNarrativeId(flowData?.narrative_id)}
                icon={<HelpPlainIcon />}
                key="help"
                rel="noreferrer"
                target="_blank"
                tooltip="Help"
              />,
            ]}
          >
            <EditableTitle
              autoFocus={false}
              className={styles['narrative-title']}
              isDisabled
              onChange={() => {}}
              value={titleValue}
            />
          </Toolbar>
        )}
      </div>
      {!isDraft && showModalShare && (
        <ModalShare
          hasSharePublicly={canShareReportsPublicly}
          ids={[flowData?.id]}
          isNarrative
          isSurvey={false}
          setShowModalShare={setShowModalShare}
          shareItemWithClients={shareNarrativeWithClientGroup}
          shareItemWithUsers={shareNarrativeWithUsers}
        />
      )}
      <TitleFooter
        answers={flowData?.answers || {}}
        createdDatetime={flowData?.createdDatetime}
        handleReprompt={handleReprompt}
        isDraft={isDraft}
        isPublicShare={isPublicShare}
        isRepromptDisabled={!canReprompt}
        narrativeFlowStatus={flowData?.status}
        selectedPrompts={footerSelectedPrompts}
      />
    </div>
  );
};

NarrativeTitle.propTypes = {
  downloadData: PropTypes.object,
  flowData: PropTypes.object,
  handleReprompt: PropTypes.func,
  isDraft: PropTypes.bool,
  onNarrativeNameChange: PropTypes.func,
  onTemplateSaved: PropTypes.func,
  publicToken: PropTypes.string,
  selectedPromptsOverride: PropTypes.array,
  setFlow: PropTypes.func,
  titleOverride: PropTypes.string,
  topicIds: PropTypes.arrayOf(PropTypes.string),
};

export default NarrativeTitle;
